import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';

interface SearchEntry {
  url: string;
  timestamp: number;
}

interface RecentSearchesState {
  savedSearches: SearchEntry[]; // Manually saved searches (pinned at top)
  recentSearches: SearchEntry[]; // Automatically saved recent searches
}

const initialState: RecentSearchesState = {
  savedSearches: [],
  recentSearches: [],
};

const recentSearchesSlice = createSlice({
  name: 'recentSearches',
  initialState,
  reducers: {
    addRecentSearch: (state, action: PayloadAction<string>) => {
      // Ensure state is properly initialized
      if (!state.savedSearches) state.savedSearches = [];
      if (!state.recentSearches) state.recentSearches = [];
      
      const url = action.payload;
      console.log('[recentSearchesSlice] addRecentSearch called with URL:', url);
      
      // Don't add to recent searches if it's already saved
      const isSaved = state.savedSearches.some((search) => search.url === url);
      if (isSaved) {
        console.log('[recentSearchesSlice] URL already in saved searches, skipping');
        return;
      }
      // Remove any existing entry with the same URL from recent searches
      state.recentSearches = state.recentSearches.filter((search) => search.url !== url);
      // Add to the beginning (most recent first)
      state.recentSearches.unshift({ url, timestamp: Date.now() });
      // Keep only the last 50 recent searches
      if (state.recentSearches.length > 50) {
        state.recentSearches = state.recentSearches.slice(0, 50);
      }
      console.log('[recentSearchesSlice] Recent searches after add:', state.recentSearches.length);
    },
    removeRecentSearch: (state, action: PayloadAction<string>) => {
      if (!state.recentSearches) state.recentSearches = [];
      state.recentSearches = state.recentSearches.filter((search) => search.url !== action.payload);
    },
    setRecentSearches: (state, action: PayloadAction<SearchEntry[]>) => {
      state.recentSearches = action.payload;
    },
    addSavedSearch: (state, action: PayloadAction<string>) => {
      // Ensure state is properly initialized
      if (!state.savedSearches) state.savedSearches = [];
      if (!state.recentSearches) state.recentSearches = [];
      
      const url = action.payload;
      // Remove from recent searches if it exists there
      state.recentSearches = state.recentSearches.filter((search) => search.url !== url);
      // Check if already saved
      const exists = state.savedSearches.some((search) => search.url === url);
      if (!exists) {
        // Add to the beginning of saved searches (most recent saved first)
        state.savedSearches.unshift({ url, timestamp: Date.now() });
      }
    },
    removeSavedSearch: (state, action: PayloadAction<string>) => {
      if (!state.savedSearches) state.savedSearches = [];
      state.savedSearches = state.savedSearches.filter((search) => search.url !== action.payload);
    },
    setSavedSearches: (state, action: PayloadAction<SearchEntry[]>) => {
      state.savedSearches = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Handle REHYDRATE to ensure state is properly initialized
    builder.addCase(REHYDRATE, (state, action: any) => {
      const persistedState = action.payload?.recentSearches;
      if (persistedState) {
        // Ensure both arrays exist and are arrays
        return {
          savedSearches: Array.isArray(persistedState.savedSearches) 
            ? persistedState.savedSearches 
            : [],
          recentSearches: Array.isArray(persistedState.recentSearches) 
            ? persistedState.recentSearches 
            : [],
        };
      }
      // If no persisted state, ensure we have the initial structure
      if (!state.savedSearches) state.savedSearches = [];
      if (!state.recentSearches) state.recentSearches = [];
      return state;
    });
  },
});

export const { 
  addRecentSearch, 
  removeRecentSearch, 
  setRecentSearches,
  addSavedSearch,
  removeSavedSearch,
  setSavedSearches
} = recentSearchesSlice.actions;
export default recentSearchesSlice.reducer;
